import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert } from 'src/components/Alert';
import { Loader } from 'src/components/Loader';
import { Meta } from 'src/components/Meta';
import { useSession } from 'src/contexts/SessionContext';
import {
  createAppAlert,
  fetchAppAlert,
  updateAppAlert,
} from 'src/models/app-alert/appalerts';
import { supportsBanners } from 'src/models/platform/utils';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import api from 'src/utils/ajax';

import { AppAlertForm } from './AppAlertForm';
import { buildInitialValues, normalizeAlertType } from './appAlertFormUtils';

import type { BannerPreviewVariant } from './_types';

const INDEX_ROUTE = '/admin/modal-alerts';
const DEFAULT_PHONE_PRESET_ID = 'iphone-14';
const DEFAULT_SCALE_ID = '1';

const NewAppAlertPage = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDeviceId, setSelectedDeviceId] = useState(
    DEFAULT_PHONE_PRESET_ID
  );
  const [selectedScaleId, setSelectedScaleId] = useState(DEFAULT_SCALE_ID);
  const [bannerPreviewVariant, setBannerPreviewVariant] =
    useState<BannerPreviewVariant>('short');
  const [uploadingFields, setUploadingFields] = useState({
    heroImageUrl: false,
    bgImageUrl: false,
  });
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appAlertsForm',
  });

  const bannersEnabled = supportsBanners(session);
  const requestedAlertType = normalizeAlertType(
    searchParams.get('alertType'),
    bannersEnabled
  );
  const pageTitle = isNew
    ? t('pageTitle.new', {
        defaultValue: 'New App Alert',
      })
    : t('pageTitle.edit', {
        defaultValue: 'Edit App Alert',
      });

  useEffect(() => {
    if (!isNew) {
      return;
    }

    if (searchParams.get('alertType') !== requestedAlertType) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('alertType', requestedAlertType);
      setSearchParams(nextParams, { replace: true });
    }
  }, [isNew, requestedAlertType, searchParams, setSearchParams]);

  const alertQuery = useQuery({
    queryKey: ['appAlert', id],
    queryFn: () => fetchAppAlert(Number(id)),
    enabled: !isNew && !!id,
  });

  const createMutation = useMutation({
    mutationFn: createAppAlert,
  });

  const updateMutation = useMutation({
    mutationFn: ({
      alertId,
      values,
    }: {
      alertId: number;
      values: Parameters<typeof updateAppAlert>[1];
    }) => updateAppAlert(alertId, values),
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isImageUploading =
    uploadingFields.heroImageUrl || uploadingFields.bgImageUrl;

  const content =
    !isNew && alertQuery.isLoading ? (
      <Loader
        size="large"
        label={t('loading', {
          defaultValue: 'Loading App Alert...',
        })}
      />
    ) : !isNew && alertQuery.isError ? (
      <Alert severity="error">
        {api.formatError(
          alertQuery.error,
          t('loadFailed', {
            defaultValue: 'Failed to load app alert',
          })
        )}
      </Alert>
    ) : (
      <AppAlertForm
        initialValues={buildInitialValues(alertQuery.data, requestedAlertType)}
        bannersEnabled={bannersEnabled}
        isNew={isNew}
        isSaving={isSaving}
        isImageUploading={isImageUploading}
        selectedDeviceId={selectedDeviceId}
        setSelectedDeviceId={setSelectedDeviceId}
        selectedScaleId={selectedScaleId}
        setSelectedScaleId={setSelectedScaleId}
        bannerPreviewVariant={bannerPreviewVariant}
        setBannerPreviewVariant={setBannerPreviewVariant}
        uploadingFields={uploadingFields}
        setUploadingFields={setUploadingFields}
        onCancel={() => navigate(INDEX_ROUTE)}
        onSubmit={async (values) => {
          if (isNew) {
            await createMutation.mutateAsync(values);
          } else if (id) {
            await updateMutation.mutateAsync({
              alertId: Number(id),
              values,
            });
          }

          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['adminAppAlerts'] }),
            queryClient.invalidateQueries({ queryKey: ['appAlerts'] }),
            queryClient.invalidateQueries({ queryKey: ['appAlert'] }),
          ]);

          navigate(INDEX_ROUTE);
        }}
      />
    );

  return (
    <>
      <Meta title={pageTitle} brand={session.brand} />
      <AppFrameConfig
        id="admin-modal-alert-form"
        containerMaxWidth="xl"
        title={pageTitle}
        backButtonTitle={t('back', { defaultValue: 'App Alerts' })}
        backButtonLinkTo={INDEX_ROUTE}
      />
      {content}
    </>
  );
};

export default NewAppAlertPage;
